<template>
    <span
        class="drag-wrapper"
        :class="{
            dragging: isDragging,
            snapping: isSnapping
        }"
    >
        <Border v-if="isDragging && border" :radius="radius" />
        <Component
            :is="target ? 'MagicPortal' : 'span'"
            ref="portal"
            :to="target"
            :disabled="!usePortal"
            :z-index="isDropped ? 1 : null"
            :class="target ? null : 'drag-inner'"
        >
            <span
                v-if="dragStyle === 'native' && (isDragging || isSnapping)"
                class="drag-placeholder"
            >
                <slot />
            </span>
            <Component
                :is="preventKeyboard ? 'span' : 'Clickable'"
                ref="draggable"
                class="drag"
                :class="[dragStyle, {
                    draggable: !disabled,
                    dragging: isDragging,
                    dropped: isDropped,
                    dropping: isDropping,
                    snapping: isSnapping,
                    shadow,
                    disabled
                }]"
                :style="{ transition, transform }"
                :aria-grabbed="isDragging"
                :tabindex="disabled || isDropped ? -1 : null"
                draggable
                @pointerdown="onDragStart"
                @pointermove="onDelayMove"
                @pointerup="cancelDrag"
                @keypress.enter="onDragStart"
                @keypress.space="onDragStart"
            >
                <slot :dropped="isDropped" />
                <span v-if="!disabled" class="visually-hidden">
                    Draggable
                </span>
            </Component>
        </Component>
    </span>
</template>

<script>
import useDrag from '@/store/drag';

import Border from '@/components/utility/border';
import Clickable from '@/components/utility/clickable';
import MagicPortal from '@/components/utility/magic-portal';

let uid = 1;
export default {
    name: 'Drag',
    components: { Border, Clickable, MagicPortal },
    props: {
        target: { type: String, default: undefined },
        delay: { type: Number, default: 0 },
        dragStyle: { type: String, default: 'move', validator: style => ['move', 'native'].includes(style) },

        boundary: { type: [HTMLElement, Object], default: undefined  },
        reverseBoundary: { type: Boolean, default: false },
        scale: { type: Number, default: 1 },

        shadow: { type: Boolean, default: true },
        border: { type: Boolean, default: false },
        radius: { type: Number, default: undefined },

        reset: { type: Boolean, default: true },
        disabled: { type: Boolean, default: false },
        preventKeyboard: { type: Boolean, default: false },
        preventHorizontal: { type: Boolean, default: false },
        preventVertical: { type: Boolean, default: false },
        bubble: { type: Boolean, default: true },
        handle: { type: Boolean, default: false },
        speed: { type: Number, default: undefined }
    },
    emits: ['position', 'drag', 'drag:before', 'drag:end', 'reset'],
    slots: ['default'],
    data() {
        return {
            uuid: null,

            offsetX: 0,
            offsetY: 0,
            dragOffsetX: 0,
            dragOffsetY: 0,
            dragStartX: 0,
            dragStartY: 0,
            dragLimit: null,
            dragDelay: null,
            transitionSpeed: 0,

            delayTimer: null,

            isDragging: false,
            isSnapping: false,
            isKeypress: false
        };
    },
    computed: {
        useDrag() {
            return useDrag;
        },
        isDropping() {
            return useDrag.isDropping;
        },
        isDropped() {
            return useDrag.isDropped(this.uuid);
        },
        isCurrentlyDragging() {
            if (!useDrag.draggingComponent) return;
            return useDrag.draggingComponent.uuid === this.uuid;
        },
        droppingElement() {
            if (!useDrag.droppingComponent) return;
            return useDrag.droppingComponent.$el;
        },
        currentOffsetX() {
            return Math.round((this.offsetX + this.dragOffsetX) * -1);
        },
        currentOffsetY() {
            return Math.round((this.offsetY + this.dragOffsetY) * -1);
        },
        transition() {
            if (!this.isSnapping) return;
            return `transform ${this.transitionSpeed}ms`;
        },
        transform() {
            if (this.handle) return null;
            if (!(this.currentOffsetX || this.currentOffsetY)) return;
            return `translate(${this.currentOffsetX}px, ${this.currentOffsetY}px)`;
        },
        usePortal() {
            return this.target && (this.isDragging || this.isCurrentlyDragging || this.isSnapping || this.isDropped);
        }
    },
    watch: {
        currentOffsetX() {
            this.$emit('position', { x: this.currentOffsetX, y: this.currentOffsetY });
        },
        currentOffsetY() {
            this.$emit('position', { x: this.currentOffsetX, y: this.currentOffsetY });
        },
        isDropped(to) {
            if (to) this.isDragging = false;
        },
        isDragging(to) {
            this.$emit('drag', to);
            useDrag.onDrag(to, this, this.isKeypress);
        },
        isCurrentlyDragging(to) {
            if (!to && this.isDragging) this.onDragEnd();
        }
    },
    created() {
        this.uuid = `draggable-${uid}`;
        uid += 1;

        useDrag.addDraggable(this);
    },
    beforeUnmount() {
        useDrag.removeDraggable(this);
    },
    methods: {
        cancelDrag() {
            if (this.dragDelay) {
                this.$cancel(this.dragDelay);
                this.dragDelay = null;
            }
        },
        onDelayMove(event) {
            if (!this.dragDelay) return;
            const LONGPRESS_MOVEMENT_THRESHOLD = this.$browser.is.touch ? 1 : 32;
            if (Math.abs(Math.max(this.dragStartX - event.clientX, this.dragStartY - event.clientY)) > LONGPRESS_MOVEMENT_THRESHOLD) {
                this.cancelDrag();
            }
        },
        onDragStart(e, hasLongpressed = false) {
            this.isKeypress = e.type === 'keypress';
            if (this.isKeypress && this.preventKeyboard) return;
            if (this.isKeypress && this.isDragging) return this.onDragEnd();
            if (this.disabled || this.isDragging || this.isSnapping) return;
            if (!this.bubble) e.stopPropagation();
            this.$emit('drag:before');

            if (this.delay && !hasLongpressed && !this.isKeypress) {
                this.dragStartX = e.clientX;
                this.dragStartY = e.clientY;
                this.dragDelay = this.$timeout(() => this.onDragStart(e, true), this.delay);
                return;
            }
            if (!this.isKeypress) {
                document.addEventListener('pointerup', this.onDragEnd);
                document.addEventListener('pointermove', this.onDrag);
            }

            if (this.boundary) {
                const el = this.$refs.draggable.$el || this.$refs.draggable;
                const draggable = el.getBoundingClientRect();
                const boundary = this.boundary.getBoundingClientRect();

                this.dragLimit = {
                    left: Math.round(draggable.left - boundary.left) * (1 / this.scale),
                    top: Math.round(draggable.top - boundary.top) * (1 / this.scale),
                    right: Math.round(draggable.right - boundary.right) * (1 / this.scale),
                    bottom: Math.round(draggable.bottom - boundary.bottom) * (1 / this.scale)
                };
            }

            e.preventDefault();
            this.isDragging = true;
            this.dragStartX = e.clientX;
            this.dragStartY = e.clientY;
        },
        onDrag(e) {
            if (!this.isDragging) return;
            e.preventDefault();

            const deltaX = (this.dragStartX - e.clientX) * (1 / this.scale);
            const deltaY = (this.dragStartY - e.clientY) * (1 / this.scale);

            let preventX = this.preventHorizontal, preventY = this.preventVertical;
            if (this.boundary && this.dragLimit) {
                const isGoingLeft = deltaX > this.dragOffsetX;
                const isGoingUp = deltaY > this.dragOffsetY;

                if (this.reverseBoundary) {
                    if (!preventX) {
                        if (deltaX <= this.dragLimit.left && !isGoingLeft) {
                            this.dragOffsetX = this.dragLimit.left;
                            preventX = true;
                        }
                        if (deltaX >= this.dragLimit.right && isGoingLeft) {
                            this.dragOffsetX = this.dragLimit.right;
                            preventX = true;
                        }
                    }
                    if (!preventY) {
                        if (deltaY <= this.dragLimit.top && !isGoingUp) {
                            this.dragOffsetY = this.dragLimit.top;
                            preventY = true;
                        }
                        if (deltaY >= this.dragLimit.bottom && isGoingUp) {
                            this.dragOffsetY = this.dragLimit.bottom;
                            preventY = true;
                        }
                    }
                } else {
                    if (!preventX) {
                        if (deltaX >= this.dragLimit.left && isGoingLeft) {
                            this.dragOffsetX = this.dragLimit.left;
                            preventX = true;
                        }
                        if (deltaX <= this.dragLimit.right && !isGoingLeft) {
                            this.dragOffsetX = this.dragLimit.right;
                            preventX = true;
                        }
                    }
                    if (!preventY) {
                        if (deltaY >= this.dragLimit.top && isGoingUp) {
                            this.dragOffsetY = this.dragLimit.top;
                            preventY = true;
                        }
                        if (deltaY <= this.dragLimit.bottom && !isGoingUp) {
                            this.dragOffsetY = this.dragLimit.bottom;
                            preventY = true;
                        }
                    }
                }
            }

            if (!preventX) this.dragOffsetX = deltaX;
            if (!preventY) this.dragOffsetY = deltaY;
        },
        onDragEnd() {
            this.offsetX += this.dragOffsetX;
            this.offsetY += this.dragOffsetY;

            if (!this.isKeypress) {
                document.removeEventListener('pointerup', this.onDragEnd);
                document.removeEventListener('pointermove', this.onDrag);
            }

            this.dragOffsetX = 0;
            this.dragOffsetY = 0;
            this.isDragging = false;
            this.isKeypress = false;
            this.$emit('drag:end');

            if (this.reset && !(this.isDropping || this.isDropped)) this.resetPosition();
        },
        getTransitionSpeed(x, y, destinationX, destinationY) {
            // Calculate the distance between current location and origin
            const distance = Math.sqrt(
                Math.pow(destinationX - x, 2) +
                Math.pow(destinationY - y, 2)
            );
            return Math.round(distance / 4);
        },
        snapToDrop(animate = true) {
            if (!this.droppingElement) return Promise.resolve();
            const target = this.droppingElement.getBoundingClientRect();
            return this.snapTo(target, animate);
        },
        snapTo(target, animate = true) {
            const el = this.$refs.draggable.$el || this.$refs.draggable;
            const { left: targetX, top: targetY } = target;
            const { left: x, top: y } = el.getBoundingClientRect();
            return this.setPosition(
                (x - targetX) + this.offsetX,
                (y - targetY) + this.offsetY,
                animate
            );
        },
        resetPosition(emit = true) {
            if (this.offsetX === 0 && this.offsetY === 0) return Promise.resolve();
            if (emit) this.$emit('reset');
            return this.setPosition(0, 0);
        },
        movePosition(x = 0, y = 0) {
            this.transitionSpeed = isNaN(this.speed)
                ? this.getTransitionSpeed(this.currentOffsetX, this.currentOffsetY, x, y)
                : this.speed;
            this.isSnapping = true;

            this.offsetX += x;
            this.offsetY += y;

            return this.$timeout(() => {
                this.isSnapping = false;
                this.transitionSpeed = 0;
            }, this.transitionSpeed);
        },
        setPosition(x = 0, y = 0, animate = true) {
            if (animate) {
                this.transitionSpeed = isNaN(this.speed)
                    ? this.getTransitionSpeed(this.currentOffsetX, this.currentOffsetY, x, y)
                    : this.speed;
                this.isSnapping = true;
            }

            this.offsetX = x;
            this.offsetY = y;

            if (animate) {
                return this.$timeout(() => {
                    this.isSnapping = false;
                    this.transitionSpeed = 0;
                }, this.transitionSpeed);
            } else {
                return Promise.resolve();
            }
        },
        focus() {
            const el = this.$refs.draggable.$el || this.$refs.draggable;
            if (el instanceof Element) el.focus();
        }
    }
};
</script>

<style lang="less">

.drag-wrapper, .portal-wrapper {
    position: relative;
    .border { color: var(--steel); }
    .drag-placeholder { position: absolute; }
    > .drag-inner { display: contents; }
    .drag {
        display: block;
        position: relative;
        width: 100%;
        height: 100%;
    }
    .draggable {
        cursor: grab;
        touch-action: none;
        &.dragging, &.snapping { z-index: 99999; }
        &.dragging {
            cursor: grabbing;
            user-select: none;
        }
        &.dropped { z-index: 1; }
        &.disabled {
            cursor: default;
            touch-action: auto;
        }
        &.shadow {
            > * {
                transition: box-shadow 0.35s var(--curve);
                box-shadow: var(--shadow-base);
            }
            &:hover, &.dragging {
                > * { box-shadow: var(--shadow-xl); }
            }
        }
        &.native.dragging {
            opacity: 0.8;
        }
    }
}
.portal-wrapper {
    .draggable, .dropped {
        display: flex;
        width: 100%;
    }
    .dropped { z-index: 1; }
}
</style>
